export const HASH_LINK_MIN_CHARS = 3;
export const HASH_LINK_LIMIT_PER_KIND = 8;

export type HashLinkKind = 'dossier' | 'societe';

export type HashLinkHit = {
  kind: HashLinkKind;
  id: string;
  title: string;
  subtitle?: string;
  hint?: string | null;
};

export type HashLinkSection = {
  kind: HashLinkKind;
  label: string;
  hits: HashLinkHit[];
};

export type HashLinkToken = {
  kind: HashLinkKind;
  id: string;
  title: string;
};

export type MessageBodyPart =
  | { type: 'text'; value: string }
  | { type: 'hash'; kind: HashLinkKind; id: string; title: string }
  | { type: 'url'; value: string };

const UUID_RE = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const KIND_CODE: Record<HashLinkKind, 'd' | 's'> = { dossier: 'd', societe: 's' };
const CODE_KIND: Record<string, HashLinkKind> = { d: 'dossier', s: 'societe' };

const CLOSED_TOKEN_AT_END = new RegExp(
  `(?:^|\\s)#([ds])/${UUID_RE}\\|[^|\\n]*\\|\\s*$`,
  'i',
);
const HASH_TOKEN_RE = new RegExp(`#([ds])/(${UUID_RE})\\|([^|\\n]*)\\|`, 'gi');
const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;

const SECTION_ORDER: HashLinkKind[] = ['dossier', 'societe'];
const SECTION_LABEL: Record<HashLinkKind, string> = {
  dossier: 'Dossiers',
  societe: 'Sociétés',
};

export function detectHashQuery(textBeforeCursor: string): string | null {
  if (CLOSED_TOKEN_AT_END.test(textBeforeCursor)) return null;
  const match = textBeforeCursor.match(/(?:^|\s)#([^\n]*)$/);
  if (!match) return null;
  const query = match[1] ?? '';
  if (query.includes('|')) return null;
  if (query.trim().length < HASH_LINK_MIN_CHARS) return null;
  return query;
}

function sanitizeTitle(title: string): string {
  return title.replace(/[|\n\r]/g, '').trim();
}

export function serializeHashLink(link: HashLinkToken): string {
  return `#${KIND_CODE[link.kind]}/${link.id}|${sanitizeTitle(link.title)}|`;
}

export function applyHashLinkSelection(
  text: string,
  cursor: number,
  token: string,
): { text: string; cursor: number } {
  const before = text.slice(0, cursor);
  const after = text.slice(cursor);
  const hashAt = before.lastIndexOf('#');
  if (hashAt < 0) return { text, cursor };
  const prefix = before.slice(0, hashAt);
  const next = `${prefix}${token} ${after}`;
  const nextCursor = prefix.length + token.length + 1;
  return { text: next, cursor: nextCursor };
}

export function hashLinkPath(link: Pick<HashLinkToken, 'kind' | 'id'>): string {
  return link.kind === 'dossier' ? `/dossiers/${link.id}` : `/contacts/${link.id}`;
}

export function hashLinkLabel(title: string): string {
  return `# ${title}`;
}

export function dossierHashHint(row: {
  closedAt?: string | null;
  archivedAt?: string | null;
}): string | null {
  if (row.closedAt) return 'clôturé';
  if (row.archivedAt) return 'archivé';
  return null;
}

type IndexedPart = { start: number; end: number; part: Exclude<MessageBodyPart, { type: 'text' }> };

export function splitMessageBody(body: string): MessageBodyPart[] {
  const found: IndexedPart[] = [];

  for (const match of body.matchAll(HASH_TOKEN_RE)) {
    const start = match.index ?? 0;
    const code = match[1] ?? '';
    const kind = CODE_KIND[code];
    const id = match[2] ?? '';
    const title = match[3] ?? '';
    if (!kind || !id) continue;
    found.push({
      start,
      end: start + match[0].length,
      part: { type: 'hash', kind, id, title },
    });
  }

  for (const match of body.matchAll(URL_RE)) {
    const start = match.index ?? 0;
    const end = start + match[0].length;
    if (found.some((f) => start < f.end && end > f.start)) continue;
    found.push({ start, end, part: { type: 'url', value: match[0] } });
  }

  found.sort((a, b) => a.start - b.start);

  const parts: MessageBodyPart[] = [];
  let cursor = 0;
  for (const item of found) {
    if (item.start < cursor) continue;
    if (item.start > cursor) {
      parts.push({ type: 'text', value: body.slice(cursor, item.start) });
    }
    parts.push(item.part);
    cursor = item.end;
  }
  if (cursor < body.length) {
    parts.push({ type: 'text', value: body.slice(cursor) });
  }
  return parts;
}

export function groupHashLinkHits(
  hits: HashLinkHit[],
  limitPerKind = HASH_LINK_LIMIT_PER_KIND,
): HashLinkSection[] {
  const byKind = new Map<HashLinkKind, HashLinkHit[]>();
  for (const hit of hits) {
    const list = byKind.get(hit.kind) ?? [];
    if (list.length < limitPerKind) list.push(hit);
    byKind.set(hit.kind, list);
  }
  return SECTION_ORDER.flatMap((kind) => {
    const kindHits = byKind.get(kind);
    if (!kindHits?.length) return [];
    return [{ kind, label: SECTION_LABEL[kind], hits: kindHits }];
  });
}
